import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useFeatureFlag } from '@fs/zion-flags'
import { i18n } from '@fs/zion-locale'
import {
  Autosuggest,
  InlineButton,
  InputChips,
  QuickGlanceOverlay,
  Separator,
  Skeleton,
  useChipCollection,
  useOverlay,
} from '@fs/zion-ui'
import {
  useZionFormContext,
  useWatch,
  FormAutosuggest,
  FormDateSuggest,
  FormPlaceSuggest,
  FormSelect,
  FormTextArea,
  FormTextField,
  FormCheckbox,
} from '@fs/zion-form'
import { useHelpOverlay } from '@fs/zion-help-overlay'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Link, useQueryParams } from '@fs/zion-router'

import { formatWithRules, getCoverageDatesFromGedcomX } from 'helpers/dateUtils'
import { useGroupSearch } from 'hooks'
import getFormattedDate, { dateFormats } from 'helpers/dateFormatHelper'
import LifeEvents from './components/LifeEvents'
import { chineseSurnames, getRecordTypes } from './queries'
import styles from './FormInputs.styles'
import transl from './FormInputs.transl'

export function AncestorInput(props) {
  return <FormTextField label={transl.ancestor()} name="firstAncestor" clearable {...props} />
}

export function AncestralHallInput(props) {
  return <FormTextField label={transl.ancestralHall()} name="ancestralHall" clearable {...props} />
}

export function CameraOperatorIdInput() {
  return <FormTextField clearable name="cameraOperatorId" label={transl.cameraOperatorId()} />
}

export function CaptureIdInput() {
  return <FormTextField clearable name="externalId" label={transl.captureId()} />
}

export function ClanBranchInput() {
  return <FormTextField label={transl.clanBranch()} name="clanBranch" clearable />
}

export function ClanInput({ onChange }) {
  return <FormTextField label={transl.clan()} name="clan" onChange={onChange} clearable />
}

export function CreatorInput() {
  return <FormTextField label={transl.creator()} name="creator" clearable />
}

export function CustodianInput() {
  return <FormTextField label={transl.custodian()} name="custodian" clearable />
}

export function DateInput() {
  const { setValue, getValues } = useZionFormContext()
  const { date } = getValues()
  const { groupSearchQueryResults, loading } = useGroupSearch()
  const { query } = useQueryParams()

  useEffect(() => {
    if ((query.startDate || query.endDate) && !loading && !date) {
      const dateSearchField = groupSearchQueryResults?.selectedFields?.date
      const validSearch = !!dateSearchField
      const invalidSearchWithDate = groupSearchQueryResults === null
      if (validSearch) {
        let primaryText = dateSearchField.standardizedText
        if (query.endDate && !query.startDate) primaryText = transl.before(dateSearchField.standardizedText)
        else if (!query.endDate && query.startDate) primaryText = transl.after(dateSearchField.standardizedText)

        setValue('date', { primaryText, isStandard: true })
      } else if (invalidSearchWithDate) {
        const locale = navigator.languages[0]
        const format = formatWithRules({ fromISOString: query.startDate, toISOString: query.endDate, locale }) || ''
        setValue('date', { primaryText: format })
      }
    } else if (!query.startDate && !query.endDate) {
      // for when the "remove date" button from suggestions is clicked
      if (date !== null) {
        setValue('date', null)
        setValue('startDate', '')
        setValue('endDate', '')
      }
    }
  }, [groupSearchQueryResults, query, setValue, date, loading])

  const onSuggestionSelected = useCallback(
    (value) => {
      if (value?.standard?.formalText) {
        const [startDate, endDate] = getCoverageDatesFromGedcomX(value.standard.formalText, true)

        setValue('startDate', startDate)
        setValue('endDate', endDate)
        setValue('date', value)
      }
    },
    [setValue]
  )

  // clear input manually with backspace
  const onInputTextChange = useCallback(
    (inputText) => {
      if (!inputText && date !== null) {
        setValue('date', null)
        setValue('startDate', '')
        setValue('endDate', '')
      }
    },
    [setValue, date]
  )

  function onClearHandler() {
    setValue('date', null)
    setValue('endDate', '')
    setValue('startDate', '')
  }

  return (
    <FormDateSuggest
      clearable
      label={transl.date()}
      name="date"
      onSuggestionSelected={onSuggestionSelected}
      onClear={onClearHandler}
      initialSelectedItem={date}
      onInputTextChange={onInputTextChange}
    />
  )
}

export function DgsNumbersInput() {
  return (
    <FormTextArea
      helpMessage={LearnMore({
        buttonText: transl.learnMore(),
        text: transl.dgsInstructions(),
        helpUrl: `https://www.familysearch.org/${i18n.language}/help/helpcenter/article/what-is-a-dgs-number`,
      })}
      label={transl.imageGroupNumber()}
      name="imageGroupNumbers"
      maxLength={500}
      rows={2}
    />
  )
}

export function GenerationPoemInput(props) {
  return <FormTextField label={transl.generationPoem()} name="generationPoem" clearable {...props} />
}

export function GroupIdsInput() {
  return <FormTextArea autoComplete="off" label={transl.groupIds()} name="groupIds" />
}

function ExclusiveFormCheckbox({ label }) {
  const { setValue } = useZionFormContext()
  const allValues = ['hasRework', 'hasMissingCoverageData', 'needsToBeOrganized', 'includeDeprecatedGroups']
  const otherValues = allValues.filter((value) => value !== label)
  const val1 = useWatch({ name: otherValues[0] })
  const val2 = useWatch({ name: otherValues[1] })
  const val3 = useWatch({ name: otherValues[2] })

  useEffect(() => {
    if (val1 || val2 || val3) setValue(label, false)
  }, [val1, val2, setValue, label, val3])

  return <FormCheckbox name={label} value label={transl[label]()} />
}

export function HasReworkInput() {
  return <ExclusiveFormCheckbox label="hasRework" />
}

export function HasMissingCoverageDataInput() {
  return <ExclusiveFormCheckbox label="hasMissingCoverageData" />
}

export function NeedsToBeOrganizedInput() {
  return <ExclusiveFormCheckbox label="needsToBeOrganized" />
}

export function IncludeDeprecatedGroups() {
  return <ExclusiveFormCheckbox label="includeDeprecatedGroups" />
}

export function ImageApidInput() {
  const isDisabled = useWatch({ name: 'imageArk' })
  return <FormTextField clearable name="imageApid" label={transl.imageApid()} disabled={Boolean(isDisabled)} />
}

export function ImageArkInput() {
  const isDisabled = useWatch({ name: 'imageApid' })
  return <FormTextField name="imageArk" label={transl.imageArk()} disabled={Boolean(isDisabled)} clearable />
}

export function KeywordInput() {
  return (
    <FormTextField
      clearable
      helpMessage={LearnMore({
        buttonText: transl.learnMore(),
        text: transl.keywordWarning(),
        helpUrl: `https://www.familysearch.org/${i18n.language}/help/helpcenter/article/tips-for-using-the-keyword-field-when-searching-historical-images`,
      })}
      label={transl.keyword()}
      name="keyword"
    />
  )
}

export function LifeEventsInput({
  whatFacets = [],
  showCheckBoxes = true,
  returnLifeEventObj = false,
  disabled = false,
}) {
  return <LifeEvents {...{ whatFacets, showCheckBoxes, returnLifeEventObj, disabled }} />
}

export function MediaNumberInput() {
  const isDisabled = useWatch({ name: 'projectId' })
  return <FormTextField name="mediaNumber" label={transl.mediaNumber()} disabled={Boolean(!isDisabled)} />
}

export function ModifiedFromDateInput() {
  return <FormTextField clearable label={transl.modifiedFromDate()} name="modifiedFromDate" type="date" />
}

export function ModifiedToDateInput() {
  return <FormTextField clearable label={transl.modifiedToDate()} name="modifiedToDate" type="date" />
}

export function RomanizationSelect() {
  const ROMANIZATION_LOCALES = [
    { label: 'Mandarin Pinyin', value: 'en' },
    { label: 'Mandarin Wade-Giles', value: 'en-wadegile' },
    { label: 'Cantonese Jyutping', value: 'en-x-tongyong' },
    { label: 'Cantonese Yale', value: 'en-x-yale' },
    { label: 'Korean', value: 'ko_Latn' },
  ]

  return <FormSelect label="Select Romanization" name="romanizeLocale" options={ROMANIZATION_LOCALES} />
}

export function NaturalGroupInput() {
  return <FormTextArea name="naturalGroupIds" label={transl.naturalGroup()} />
}

export function PlaceInput({ loading, onPlaceSelected: externalOnPlaceSelected, ...props }) {
  const onPlaceSelected = useCallback(
    (selectedPlace) => externalOnPlaceSelected?.(selectedPlace),
    [externalOnPlaceSelected]
  )
  const improvedPlaceSuggestionsFlag = useFeatureFlag('emergingHomelands_lightyearReact_improvedPlaceSuggestions')
  const {
    query: { volumeSetSearch },
  } = useQueryParams()

  const placeFilterOptions = {
    jiapu: { placeHint: 'china;hong kong;macau;singapore;taiwan' },
    jokbo: { placeHint: 'korea' },
    keizu: { placeHint: 'japan' },
  }[volumeSetSearch]

  return (
    <span css={styles.placeInputWrapper}>
      {loading ? (
        <Skeleton.TextInput />
      ) : (
        <FormPlaceSuggest
          clearable
          label={transl.place()}
          name="place"
          onSuggestionSelected={onPlaceSelected}
          placeFilterOptions={improvedPlaceSuggestionsFlag.isOn ? placeFilterOptions : null}
          {...props}
        />
      )}
    </span>
  )
}

export function ProjectIdInput() {
  return <FormTextArea name="projectId" label={transl.projectId()} helpMessage={transl.projectIdHelpMessage()} />
}

export { default as PlacesWithin } from '../PlacesWithin/PlacesWithin'

export function RecordTypesInput({ page = 'search' }) {
  const [suggestions, setSuggestions] = useState([])
  const [inputText, setInputText] = useState('')

  const { data, loading } = useQuery(getRecordTypes, { errorPolicy: 'all' })
  const { groupSearchQueryResults } = useGroupSearch()

  const watchChips = useWatch({ name: 'recordTypeIds' })

  const recordTypeRef = useRef()
  const chipCollection = useChipCollection([])
  const { addChip, chips, resetChips, removeChip } = chipCollection
  const { setValue } = useZionFormContext()

  const recordTypes = useMemo(() => {
    if (data && !loading) {
      return data.recordTypes.map((rt) => ({
        primaryText: rt.name,
        key: rt.id,
        chipName: rt.name,
      }))
    }
    return []
  }, [data, loading])

  const initialTypes = useMemo(() => {
    if (page === 'landing' || page === 'search' || (!loading && groupSearchQueryResults?.totalCount === 0)) {
      return recordTypes
    }
    return []
  }, [recordTypes, groupSearchQueryResults, page, loading])

  // Take out suggestions that have already been picked
  useEffect(() => {
    if (Array.isArray(chips) && Array.isArray(initialTypes)) {
      const newSuggestions = [...initialTypes]
      chips.forEach((chip) => {
        const index = newSuggestions.findIndex((el) => el.key === chip.value)
        newSuggestions.splice(index, 1)
      })
      setSuggestions(newSuggestions)
    }
  }, [chips, initialTypes])

  useEffect(() => {
    if (watchChips === 'form-reset') {
      resetChips()
      setValue('recordTypeIds', '')
      setInputText('')
      return
    }

    if (page === 'landing') return
    // add chips on search results page that were added on landing page search
    if (watchChips && initialTypes) {
      let ids
      if (typeof watchChips === 'string') ids = watchChips.split(',')
      else if (Array.isArray(watchChips)) ids = watchChips
      else ids = []

      ids.forEach((id) => {
        const item = initialTypes.find((el) => parseInt(el.key, 10) === parseInt(id, 10))
        if (item != null) {
          addChip({
            value: item.key,
            label: page === 'search' ? item.primaryText : item.name,
          })
        }
      })
      // removes chips when user clicks the Remove Record Type button in search suggestions
    } else if (watchChips === undefined) resetChips()
    // updates chips on new search
    else if (initialTypes) {
      const isUpdated = chips.reduce((bool, chip) => {
        if (!bool) return false
        const correctValue = initialTypes.find((type) => type.primaryText === chip.label)
        return !!correctValue
      }, true)
      if (isUpdated) return
      chips.forEach((chip) => {
        const initialValue = initialTypes.find((type) => type.key === chip.value)
        if (initialValue == null) return
        removeChip(chip)
        requestAnimationFrame(() => {
          addChip({ label: initialValue.primaryText, value: initialValue.key })
        })
      })
    }
  }, [addChip, chips, initialTypes, page, removeChip, resetChips, watchChips, setValue])

  const onSuggestionSelected = useCallback(
    (chip) => {
      const isTextInputFocused = recordTypeRef.current === document.activeElement // chip only added if user clicks on suggestion
      if (chip && isTextInputFocused) {
        const formattedChip = {
          value: chip.key,
          label: chip.primaryText,
        }
        addChip(formattedChip)
        const newChipList = [...chips, formattedChip]
        setValue('recordTypeIds', newChipList.map((c) => c.value).join(','))
        setInputText('') // clear input text after selection
      }
    },
    [addChip, chips, setValue]
  )
  function onDismissChip(chip) {
    if (watchChips.includes(chip.value)) {
      const newChipList = chips.filter((c) => c.value !== chip.value)
      setValue('recordTypeIds', newChipList.map((c) => c.value).join(','))
    }
  }

  // this function prioritizes the exact matching record before the alphabetical order
  function sortFunction(inputValue, { primaryText: suggestionA }, { primaryText: suggestionB }) {
    if (inputValue && suggestionA && suggestionB) {
      if (inputValue.toLowerCase() === suggestionA.toLowerCase()) {
        return -1
      }
      if (inputValue.toLowerCase() === suggestionB.toLowerCase()) {
        return 1
      }
      if (suggestionA.toLowerCase() < suggestionB.toLowerCase()) {
        return -1
      }
      if (suggestionB.toLowerCase() < suggestionA.toLowerCase()) {
        return 1
      }
    }
    return 0
  }

  const label = transl.recordType()
  const clearable = true
  const highlightMatchingText = true
  const virtualize = true
  const helpMessage = LearnMore({
    buttonText: transl.learnMore(),
    helpUrl: `https://www.familysearch.org/${i18n.language}/help/helpcenter/article/tips-for-using-the-record-type-field-when-searching-historical-images`,
    text: transl.recordTypeWarning(),
  })

  const suggestProps = {
    inputText,
    onInputTextChange: (x) => setInputText(x),
    label,
    name: 'recordsInput',
    clearable,
    suggestions,
    highlightMatchingText,
    sortFunction,
    onSuggestionSelected,
    virtualize,
    helpMessage,
    clearOnClickAway: true,
  }

  return (
    <div css={styles.recordsType}>
      <Autosuggest {...suggestProps} ref={recordTypeRef} />
      <InputChips chipCollection={chipCollection} onDismiss={onDismissChip} />
    </div>
  )
}

export function SurnameInput({ onChange, isInNewForm, ...props }) {
  const [getSurnames, { data }] = useLazyQuery(chineseSurnames)
  const { query } = useQueryParams()
  const [inputText, setInputText] = useState('')
  const { setValue } = useZionFormContext()
  const useNewSurnamePicker = !i18n.language.startsWith('zh') && query.volumeSetSearch === 'jiapu'

  function handleInputChange(newInput) {
    setInputText(newInput)
  }

  useEffect(() => {
    if (useNewSurnamePicker) {
      getSurnames()
    }
  }, [getSurnames, useNewSurnamePicker])

  function handleSuggestionSelected(suggestion) {
    // NOTE: The key "INPUT_TEXT_OPTION" indicates a value provided by the user instead of an item from the list
    if (suggestion?.key === 'INPUT_TEXT_OPTION') {
      setValue('surname', { ...suggestion, value: suggestion.primaryText })
    }
  }

  const sharedSurnameProps = {
    clearable: true,
    label: transl.surname(),
    name: 'surname',
    ...props,
  }

  if (!i18n.language.startsWith('zh') && query.volumeSetSearch === 'jiapu') {
    sharedSurnameProps.placeholder = transl.surnamePlaceholder()
  }

  const surnames = data?.surnames

  if (useNewSurnamePicker) {
    const suggestions = surnames
      ?.map(({ id, romanizations, surname }) => {
        const pinyin = romanizations.find(({ scheme }) => scheme === 'pinyin')?.value
        const jyutping = romanizations.find(({ scheme }) => scheme === 'jyutping')?.value
        const wadegiles = romanizations.find(({ scheme }) => scheme === 'wade_giles')?.value
        const romanizationValues = [pinyin, jyutping, wadegiles].filter(Boolean).join(', ')

        return {
          key: id,
          primaryText: `${surname} ${romanizationValues}`,
          value: surname,
        }
      })
      .filter((suggestion) => {
        if (!inputText) return true
        if (!suggestion?.primaryText) return false
        return suggestion.primaryText.toLowerCase().includes(inputText.toLowerCase().replace(/\s/g, ''))
      })

    return (
      <FormAutosuggest
        {...sharedSurnameProps}
        handleInput
        inputText={inputText}
        noFilter
        onInputTextChange={handleInputChange}
        onSuggestionSelected={handleSuggestionSelected}
        suggestions={suggestions}
      />
    )
  }

  return <FormTextField {...sharedSurnameProps} onChange={onChange} />
}

export function TitleNumberInput() {
  return <FormTextField label={transl.titleNumber()} name="titleNumbers" clearable />
}

export function WeekEndingDateInput() {
  const warningValidate = (value) => {
    if (!value) return undefined // Allow empty values

    // Check YYYY-MM-DD format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(value)) {
      return transl.dateMustBeInYearFormat()
    }

    // Additional validation to check if it's a valid date
    const [year, month, day] = value.split('-').map(Number)
    const date = new Date(year, month - 1, day) // month is 0-indexed in Date constructor
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return transl.dateMustBeInYearFormat()
    }

    return undefined
  }

  return (
    <FormTextField
      label={transl.weekEndingDate()}
      name="originalWeekEnding"
      helpMessage={transl.dateMustBeInYearFormat()}
      placeholder="YYYY-MM-DD"
      clearable
      warningValidate={warningValidate}
    />
  )
}

export function LearnMore({ buttonText, text, helpUrl }) {
  const helpOverlay = useHelpOverlay(helpUrl)

  return (
    <span css={styles.learnMore}>
      {text}
      <InlineButton ref={helpOverlay.targetRef} onClick={helpOverlay.handleClick}>
        <div css={styles.learnMoreLink}>{buttonText}</div>
      </InlineButton>
    </span>
  )
}

LearnMore.propTypes = {
  buttonText: PropTypes.string,
  text: PropTypes.string,
}

export function HintOverlayButton({ buttonText, external, overlayText, to }) {
  const overlay = useOverlay()
  return (
    <>
      <span css={styles.inlineButton}>
        <InlineButton onClick={overlay.handleClick}>{buttonText}</InlineButton>
      </span>
      <QuickGlanceOverlay {...overlay} maxWidth="250px" placement={['start', 'bottom', 'top']}>
        {overlayText}
        {to && (
          <>
            <span>{` `}</span>
            <Link external={external} to={to}>
              {transl.learnMore()}
            </Link>
          </>
        )}
      </QuickGlanceOverlay>
    </>
  )
}

HintOverlayButton.propTypes = {
  buttonText: PropTypes.string,
  external: PropTypes.bool,
  overlayText: PropTypes.string,
  to: PropTypes.string,
}

export function StartDateAvailable({ setExpanderOpen }) {
  const { groupSearchQueryResults: searchResults, groupSearchQueryParams: queryParams } = useGroupSearch()
  const startDateFromTimestamp = searchResults?.selectedFields?.startDateAvailable?.fromTimestamp
  const startDateToTimestamp = searchResults?.selectedFields?.startDateAvailable?.startDateToTimestamp
  const startDate = getFormattedDate(startDateFromTimestamp, startDateToTimestamp, dateFormats.formatted)
  const hasStartDateAvailable = Boolean(queryParams?.startDateAvailable && startDate)

  const chipCollection = useChipCollection()
  const { addChip, resetChips } = chipCollection
  const { setValue } = useZionFormContext()
  useEffect(() => {
    if (hasStartDateAvailable) {
      resetChips()
      addChip({
        label: transl.availableSince(startDate),
        value: startDate,
        'aria-label': transl.availableSince(startDate),
      })
      setExpanderOpen(true)
    }
  }, [addChip, hasStartDateAvailable, resetChips, setExpanderOpen, startDate])

  return hasStartDateAvailable ? (
    <>
      <InputChips
        chipCollection={chipCollection}
        onDismiss={() => {
          setValue('startDateAvailable', '')
        }}
      />
      <Separator size="xs" />
    </>
  ) : null
}

export function ReadyForAuditReview() {
  const { groupSearchQueryParams: queryParams } = useGroupSearch()
  const hasSupervisorReviewActions = queryParams?.hasSupervisorReviewActions === 'true'

  const chipCollection = useChipCollection()
  const { addChip, resetChips } = chipCollection
  const { setValue } = useZionFormContext()
  useEffect(() => {
    if (hasSupervisorReviewActions) {
      resetChips()
      if (hasSupervisorReviewActions) {
        addChip({
          label: transl.availableForReview(),
          value: 'Available',
          'aria-label': transl.availableForReview(),
        })
      }
    }
  }, [addChip, hasSupervisorReviewActions, resetChips])

  return hasSupervisorReviewActions ? (
    <>
      <Separator size="xs" />
      <InputChips
        chipCollection={chipCollection}
        onDismiss={() => {
          setValue('hasSupervisorReviewActions', '')
        }}
      />
      <Separator size="xs" />
    </>
  ) : null
}
